import { create } from 'zustand';
import { axiosClient } from '@/api/axiosClient';
import { queryClient } from '@/api/queryClient';
import {
  User,
  AuthResponse,
  getStoredToken,
  getStoredUser,
  setStoredToken,
  setStoredRefreshToken,
  setStoredUser,
  clearAuthData,
} from '@/lib/auth';
import { useLanguageStore } from './languageStore';
import { useThemeStore } from './themeStore';
import { trackEvent, identifyUser } from '@/lib/analytics';

interface AuthState {
  user: User | null;
  loading: boolean;
  initialized: boolean;
  initialize: () => void;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    name: string,
    phone?: string,
    utm?: { source?: string; medium?: string; campaign?: string; term?: string; content?: string }
  ) => Promise<void>;
  logout: () => void;
  setUser: (user: User | null) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: true,
  initialized: false,

  initialize: () => {
    const token = getStoredToken();
    const storedUser = getStoredUser();

    if (token && storedUser) {
      // Trust localStorage initially - show UI immediately
      set({ user: storedUser, loading: false, initialized: true });

      // Sync language from stored user
      useLanguageStore.getState().syncFromUser(storedUser.language);

      // Identify user for analytics (returning user, with hashed email for FB Pixel)
      identifyUser(storedUser.uid, { email: storedUser.email, name: storedUser.name });

      // Validate token in background with the Content4Life backend
      axiosClient
        .get<User>('/auth/me')
        .then((response) => {
          const user = response.data;
          setStoredUser(user);
          set({ user });
          // Sync language from backend (in case it changed)
          useLanguageStore.getState().syncFromUser(user.language);
        })
        .catch((error) => {
          // Only logout on explicit 401 (invalid/expired token)
          // Network errors shouldn't force logout - user stays logged in
          if (error.response?.status === 401) {
            clearAuthData();
            set({ user: null });
          }
        });
    } else {
      clearAuthData();
      set({ user: null, loading: false, initialized: true });
    }
  },

  login: async (email: string, password: string) => {
    // Clear any previous user data including last project
    clearAuthData();
    queryClient.clear();

    const response = await axiosClient.post<AuthResponse>('/auth/login', {
      email: email.toLowerCase(),
      password,
    });

    const { access_token, refresh_token, user } = response.data;
    setStoredToken(access_token);
    if (refresh_token) {
      setStoredRefreshToken(refresh_token);
    }
    setStoredUser(user);
    set({ user });
    // Sync language from user
    useLanguageStore.getState().syncFromUser(user.language);
    // Reset theme to light on new login
    useThemeStore.getState().resetToLight();

    // Track login event and identify user (with hashed email for FB Pixel)
    trackEvent('login', { method: 'email' });
    identifyUser(user.uid, { email: user.email, name: user.name });
  },

  register: async (
    email: string,
    password: string,
    name: string,
    phone?: string,
    utm?: { source?: string; medium?: string; campaign?: string; term?: string; content?: string }
  ) => {
    // Clear any previous user data including last project
    clearAuthData();
    void phone;

    const response = await axiosClient.post<AuthResponse>('/auth/register', {
      email: email.toLowerCase(),
      password,
      name,
      utm_source: utm?.source || undefined,
      utm_medium: utm?.medium || undefined,
      utm_campaign: utm?.campaign || undefined,
      utm_term: utm?.term || undefined,
      utm_content: utm?.content || undefined,
    });

    const { access_token, refresh_token, user } = response.data;
    setStoredToken(access_token);
    if (refresh_token) {
      setStoredRefreshToken(refresh_token);
    }
    setStoredUser(user);
    set({ user });
    // Sync language from user (new users get default language)
    useLanguageStore.getState().syncFromUser(user.language);
    // Reset theme to light on new registration
    useThemeStore.getState().resetToLight();

    // Track sign_up event and identify user (with hashed email for FB Pixel)
    trackEvent('sign_up', { method: 'email' });
    identifyUser(user.uid, { email: user.email, name: user.name });
  },

  logout: () => {
    clearAuthData();
    queryClient.clear();
    set({ user: null });
  },

  setUser: (user) => set({ user }),
}));
